import {
  GRANTABLE_ROLES,
  SPECIALTIES,
  detectTimeZone,
  isValidTimeZone,
  roleLabel,
  specialtyLabel,
  type GrantableRole,
  type Specialty,
  type StaffMember,
} from "@so/shared";
import { useState } from "react";
import { useCreateStaff, useMe, useSetStaffEnabled, useStaff } from "../api/queries.js";
import {
  Alert,
  Badge,
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Textarea,
  cx,
} from "../components/ui.js";

const COMMON_ZONES = [
  "America/Toronto",
  "America/New_York",
  "America/Vancouver",
  "Europe/London",
  "Europe/Istanbul",
  "Europe/Berlin",
  "Asia/Kolkata",
  "Asia/Dubai",
  "Asia/Singapore",
  "Asia/Bangkok",
  "Asia/Seoul",
  "Australia/Sydney",
];

function StaffRow({ member, selfId }: { readonly member: StaffMember; readonly selfId: string }) {
  const setEnabled = useSetStaffEnabled();
  const isSelf = member.userId === selfId;

  return (
    <Card as="li" className={cx("px-5 py-4", !member.enabled && "opacity-60")}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-[220px] flex-1">
          <p className="text-[15.5px] font-medium">
            {member.name}
            {isSelf && <span className="text-ink-faint ml-2 text-[13px] font-normal">you</span>}
          </p>
          <p className="text-ink-soft mt-0.5 text-[13.5px]">{member.email}</p>
          <p className="text-ink-faint mt-1 font-mono text-[12.5px]">
            {member.timezone ?? "no timezone set"}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {member.roles.map((role) => (
            <Badge key={role} tone={role === "admin" ? "accent" : "neutral"}>
              {roleLabel(role)}
            </Badge>
          ))}

          {member.specialty !== undefined && (
            <Badge>{specialtyLabel(member.specialty as Specialty)}</Badge>
          )}

          {/* Cognito keeps a new account here until they set their own
              password, so an invitation that was never acted on is visible
              rather than looking like a working account. */}
          {member.status === "FORCE_CHANGE_PASSWORD" && (
            <Badge tone="caution">Invitation not yet accepted</Badge>
          )}

          {member.profile_missing === true && (
            <Badge tone="warn">No clinical profile</Badge>
          )}

          {!member.enabled && <Badge tone="warn">Disabled</Badge>}

          {/* Disabling yourself would lock the organisation out of every
              cross-patient screen, so the server refuses it and the button is
              not offered. */}
          {!isSelf && (
            <Button
              variant={member.enabled ? "secondary" : "primary"}
              loading={setEnabled.isPending}
              onClick={() =>
                void setEnabled.mutateAsync({ userId: member.userId, enabled: !member.enabled })
              }
            >
              {member.enabled ? "Disable" : "Re-enable"}
            </Button>
          )}
        </div>
      </div>

      {member.profile_missing === true && (
        <p className="text-warn mt-3 text-[13px] leading-relaxed">
          This specialist can sign in but has no clinical profile, so patients cannot find or book
          them.
        </p>
      )}
    </Card>
  );
}

function AddStaffForm({ onDone }: { readonly onDone: () => void }) {
  const create = useCreateStaff();

  const [role, setRole] = useState<GrantableRole>("clinician");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState(detectTimeZone());

  const [specialty, setSpecialty] = useState<Specialty>("oncology");
  const [credentials, setCredentials] = useState("");
  const [bio, setBio] = useState("");
  const [hospital, setHospital] = useState("");
  const [country, setCountry] = useState("");
  const [years, setYears] = useState("10");
  const [languages, setLanguages] = useState("en");

  const isClinician = role === "clinician";
  const zoneValid = isValidTimeZone(timezone);

  async function submit() {
    await create.mutateAsync({
      email: email.trim(),
      name: name.trim(),
      role,
      timezone: timezone.trim(),
      ...(isClinician
        ? {
            profile: {
              specialty,
              credentials: credentials.trim(),
              bio: bio.trim(),
              hospital: hospital.trim(),
              country: country.trim().toUpperCase(),
              years_experience: Number(years),
              languages: languages
                .split(",")
                .map((l) => l.trim())
                .filter((l) => l !== ""),
            },
          }
        : {}),
    });

    onDone();
  }

  return (
    <Card className="p-5">
      <h2 className="mb-1 text-[17px] font-semibold">Add a member of staff</h2>
      <p className="text-ink-soft mb-5 text-[14px] leading-relaxed">
        They will be emailed a one-time password and asked to choose their own on first sign-in.
        Nobody here ever sees it.
      </p>

      <div className="space-y-4">
        <Field label="Role" required>
          <Select value={role} onChange={(e) => setRole(e.target.value as GrantableRole)}>
            {GRANTABLE_ROLES.map((value) => (
              <option key={value} value={value}>
                {roleLabel(value)}
              </option>
            ))}
          </Select>
        </Field>

        {role === "admin" && (
          <Alert tone="caution">
            A care coordinator can see every patient&apos;s consultation, act on their behalf, and
            add or disable other staff — including other coordinators. Every action they take is
            recorded against their name.
          </Alert>
        )}

        <Field label="Full name" required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={isClinician ? "Dr. Wei Chen" : "Marie Lefebvre"}
          />
        </Field>

        <Field label="Email address" required hint="Where the invitation is sent.">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>

        <Field
          label="Timezone"
          required
          hint="Everything they see is rendered in this zone."
          error={timezone !== "" && !zoneValid ? "Not a recognised IANA timezone." : undefined}
        >
          <Input
            list="common-zones"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="font-mono"
          />
          <datalist id="common-zones">
            {COMMON_ZONES.map((zone) => (
              <option key={zone} value={zone} />
            ))}
          </datalist>
        </Field>

        {isClinician && (
          <>
            <div className="border-line border-t pt-4">
              <h3 className="text-ink-soft mb-3 font-mono text-[11.5px] font-medium tracking-[0.08em] uppercase">
                Clinical profile
              </h3>
            </div>

            <Field label="Specialty" required>
              <Select value={specialty} onChange={(e) => setSpecialty(e.target.value as Specialty)}>
                {SPECIALTIES.map((value) => (
                  <option key={value} value={value}>
                    {specialtyLabel(value)}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Credentials" required>
              <Input
                value={credentials}
                onChange={(e) => setCredentials(e.target.value)}
                placeholder="MBBS, MD, DM (Medical Oncology)"
              />
            </Field>

            <Field
              label="Biography"
              required
              hint="Patients choose a specialist on this. Say what they review and what they are known for."
            >
              <Textarea rows={4} value={bio} onChange={(e) => setBio(e.target.value)} />
            </Field>

            <Field label="Hospital" required>
              <Input
                value={hospital}
                onChange={(e) => setHospital(e.target.value)}
                placeholder="Apollo Cancer Centre, Chennai"
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Country" required hint="ISO code">
                <Input
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  maxLength={2}
                  placeholder="SG"
                  className="font-mono uppercase"
                />
              </Field>

              <Field label="Years" required>
                <Input
                  type="number"
                  min={0}
                  max={70}
                  value={years}
                  onChange={(e) => setYears(e.target.value)}
                />
              </Field>

              <Field label="Languages" required hint="Comma separated">
                <Input
                  value={languages}
                  onChange={(e) => setLanguages(e.target.value)}
                  placeholder="en, zh, ms"
                />
              </Field>
            </div>
          </>
        )}

        {create.error !== null && <ErrorNote error={create.error} />}

        <div className="flex gap-2">
          <Button
            loading={create.isPending}
            disabled={!zoneValid || email.trim() === "" || name.trim() === ""}
            onClick={() => void submit()}
          >
            Create account and send invitation
          </Button>
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </div>
    </Card>
  );
}

/**
 * Staff administration.
 *
 * The only screen that changes what another account is allowed to do. Patients
 * are absent by design: they arrive by self-signup, and an operator creating
 * one by hand would be creating an account somebody else is supposed to own.
 */
export function Staff() {
  const { data: me } = useMe();
  const { data, isPending, error } = useStaff();
  const [adding, setAdding] = useState(false);

  if (isPending) return <Loading label="Loading staff" />;
  if (error) return <ErrorNote error={error} />;

  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="Staff"
        actions={
          adding ? undefined : <Button onClick={() => setAdding(true)}>Add staff</Button>
        }
      >
        Specialists and care coordinators. Creating an account here grants a clinical or
        cross-patient role, so every addition and every change is recorded against your name.
      </PageHeader>

      {adding && (
        <div className="mb-6">
          <AddStaffForm onDone={() => setAdding(false)} />
        </div>
      )}

      <ul className="space-y-2.5">
        {data.staff.map((member) => (
          <StaffRow key={member.userId} member={member} selfId={me?.userId ?? ""} />
        ))}
      </ul>
    </>
  );
}
